import apiClient, { checkAdminStatus } from './apiClient'
import router from '@/router/index'

export const login = async (username, password) => {
  try {
    const response = await apiClient.post('/auth/login/', {
      username,
      password
    })
    
    if (response.data.access) {
      // Limpiar primero cualquier estado previo
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('maintenance_bypass');
      localStorage.removeItem('maintenance_bypass_expiry');
      localStorage.removeItem('maintenance_mode');
      
      // =====================================================
      // GUARDAR TOKENS - access y refresh
      // =====================================================
      localStorage.setItem('accessToken', response.data.access)
      
      // IMPORTANTE: Guardar también el refresh token para renovación automática
      if (response.data.refresh) {
        localStorage.setItem('refreshToken', response.data.refresh)
        console.log('[Auth] Refresh token guardado')
      }
      
      // CRÍTICO: Obtener correctamente el estado de admin
      // Verificar explícitamente que la respuesta tiene los campos y son booleanos
      // Usar is_staff o is_admin, cualquiera que esté disponible
      const isAdmin = response.data.is_staff === true || response.data.is_admin === true;
      
      // MOSTRAR INFORMACIÓN DE DEPURACIÓN DETALLADA
      console.log('DEPURACIÓN LOGIN - Respuesta del servidor:', {
        is_staff: response.data.is_staff,
        is_admin: response.data.is_admin,
        tipo_is_staff: typeof response.data.is_staff,
        tipo_is_admin: typeof response.data.is_admin,
        valor_interpretado: isAdmin
      });
      
      // Guardar en localStorage con conversión explícita a string
      localStorage.setItem('isAdmin', isAdmin === true ? 'true' : 'false');
      localStorage.setItem('username', username);
      
      // Para debugging - imprimir estado final
      console.log('Login exitoso. Estado de admin almacenado:', localStorage.getItem('isAdmin'));
      
      // SOLO si es admin, forzar maintenance_mode a false
      // Para usuarios normales, se respeta el valor del servidor
      if (isAdmin) {
        localStorage.setItem('maintenance_mode', 'false');
      } else {
        // Para usuarios no-admin, verificar si hay mantenimiento activo
        try {
          const maintenanceService = (await import('@/services/maintenanceService')).default;
          const inMaintenance = await maintenanceService.isInMaintenance(true); // forzar verificación
          
          // Si hay mantenimiento y no es admin, redirigir a mantenimiento después del login
          if (inMaintenance) {
            console.log('Login exitoso, pero sitio en mantenimiento. Redirigiendo a mantenimiento.');
            router.push({ name: 'Maintenance' });
          }
        } catch (e) {
          console.error('Error verificando estado de mantenimiento después del login:', e);
        }
      }
      
      // Reiniciar cache de mantenimiento al hacer login
      try {
        const maintenanceService = (await import('@/services/maintenanceService')).default;
        await maintenanceService.resetMaintenanceCache(false);
      } catch (e) {
        console.error('Error al reiniciar cache de mantenimiento:', e);
      }
      
      return response.data
    }
    
    throw new Error('No se recibió token de acceso')
  } catch (error) {
    if (error.response && error.response.data) {
      throw error;
    }
    throw new Error('Error al iniciar sesión');
  }
}

export const register = async (userData) => {
  const response = await apiClient.post('/auth/register/', userData);
  
  if (response.data) {
    return {
      message: response.data.message,
      email: response.data.email,
      userId: response.data.user_id,
      resend: response.data.resend
    };
  }
  
  throw new Error(response.data?.message || 'Error en el registro');
};

export const logout = () => {
  // Limpiar todos los tokens y datos de sesión
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')  // También eliminar refresh token
  localStorage.removeItem('isAdmin')
  localStorage.removeItem('username')
  localStorage.removeItem('userId')
  localStorage.removeItem('userEmail')
  localStorage.removeItem('maintenance_mode')
  localStorage.removeItem('maintenance_bypass')
  localStorage.removeItem('maintenance_bypass_expiry')
  console.log('[Auth] Sesión cerrada correctamente')
}

export const isAuthenticated = () => {
  return localStorage.getItem('accessToken') !== null
}

export const isAdmin = async (forceCheck = false) => {
  // IMPORTANTE: Esta función es CRUCIAL para el acceso en mantenimiento
  console.log('[Auth] Verificando estado admin, forzado:', forceCheck);
  
  // Primero revisar el localStorage para respuesta inmediata
  const localAdminStatus = localStorage.getItem('isAdmin') === 'true';
  const token = localStorage.getItem('accessToken');
  
  // Verificación rápida: sin token, no puede ser admin
  if (!token) {
    console.log('[Auth] Sin token de acceso, no puede ser admin');
    localStorage.setItem('isAdmin', 'false');
    return false;
  }
  
  // Si no es necesario forzar la verificación y tenemos un valor local, usarlo
  if (!forceCheck && localAdminStatus) {
    console.log('[Auth] Usando estado admin desde localStorage:', localAdminStatus);
    
    // SIEMPRE ASEGURAR QUE UN ADMIN TENGA MANTENIMIENTO DESACTIVADO
    if (localAdminStatus) {
      localStorage.setItem('maintenance_mode', 'false');
    }
    
    return localAdminStatus;
  }
  
  // Verificar con el servidor si el usuario realmente es administrador
  try {
    // Usar endpoint específico para verificación de admin
    const isAdminStatus = await checkAdminStatus(true); // Forzar verificación con servidor
    console.log('[Auth] Verificación de servidor de estado admin:', isAdminStatus);
    
    // Actualizar el localStorage con el valor más reciente
    localStorage.setItem('isAdmin', isAdminStatus ? 'true' : 'false');
    
    // Si es admin, SIEMPRE asegurar que maintenance_mode esté desactivado
    if (isAdminStatus) {
      localStorage.setItem('maintenance_mode', 'false');
      console.log('[Auth] Admin confirmado, desactivando modo mantenimiento');
    }
    
    return isAdminStatus;
  } catch (error) {
    console.error('[Auth] Error al verificar estado de administrador:', error);
    
    // Si hay error pero tenemos un token y localmente es admin, confiar en eso
    if (token && localAdminStatus) {
      console.log('[Auth] Usando estado admin local debido al error');
      localStorage.setItem('maintenance_mode', 'false');
      return true;
    }
    
    return false; // En caso de error y sin confirmación previa, asumir no admin
  }
}

export const getUsername = () => {
  return localStorage.getItem('username')
}

export const getAccessToken = () => {
  return localStorage.getItem('accessToken')
}

export const getUserInfo = async () => {
  try {
    const token = getAccessToken()
    const response = await apiClient.get('/auth/profile/', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al obtener información del usuario')
  }
}

export const updateUserProfile = async (userData) => {
  try {
    const response = await apiClient.put('/auth/profile/', userData)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Error al actualizar el perfil'
    throw new Error(errorMessage)
  }
}

export const changePassword = async ({old_password, new_password}) => {
  try {
    const response = await apiClient.post('/auth/change-password/', {
      old_password,
      new_password
    })
    // Update token after password change if provided
    if (response.data.access) {
      localStorage.setItem('accessToken', response.data.access)
    }
    return response.data
  } catch (error) {
    throw error.response?.data || error
  }
}

export const verifyEmail = async (data) => {
  const verificationData = {
    email: data.email,
    code: data.code
  };
  const response = await apiClient.post('/auth/verify-email/', verificationData);
  return response.data;
};

export const resendVerification = async (email) => {
  const response = await apiClient.post('/auth/resend-verification/', {
    email
  });
  return response.data;
};

export const requestVerificationCode = async (type, newEmail = null, password = null) => {
  try {
    const token = getAccessToken()
    const response = await apiClient.post('/auth/verify-change/', {
      type,
      new_email: newEmail,
      password: password
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    return response.data
  } catch (error) {
    console.error('Verification code request error:', error.response?.data || error) // Debug
    
    // Manejar casos específicos de error
    if (error.response?.data) {
      // Contraseña incorrecta
      if (error.response.data.password && Array.isArray(error.response.data.password)) {
        throw { error: 'invalid_password', message: 'La contraseña es incorrecta' };
      }
      
      // Email ya existente
      if (error.response.data.new_email && Array.isArray(error.response.data.new_email)) {
        const emailErrors = error.response.data.new_email;
        if (emailErrors.some(err => err.includes('exist') || err.includes('registrado'))) {
          throw { error: 'email_already_exists', message: 'Este email ya está registrado por otro usuario' };
        }
      }

      // Si hay un mensaje de error específico
      if (typeof error.response.data === 'object' && error.response.data.error) {
        throw error.response.data;
      }
    }
    
    // Error genérico si no pudimos identificar uno específico
    throw error.response?.data || { error: 'unknown_error', message: 'Error al solicitar verificación' };
  }
}

export const updateEmailWithVerification = async (email, verificationCode) => {
  try {
    const response = await apiClient.put('/auth/profile/', {
      email,
      verification_code: verificationCode
    })
    return response.data
  } catch (error) {
    // Mejor manejo de errores para diferentes casos
    if (error.response?.data) {
      // Si es un error relacionado con email ya existente
      if (error.response.data.email && Array.isArray(error.response.data.email)) {
        const emailErrors = error.response.data.email;
        if (emailErrors.some(err => err.includes('exist'))) {
          throw { error: 'email_already_exists', message: 'Este email ya está registrado' };
        }
      }
      
      // Si es un error relacionado con código inválido
      if (error.response.data.verification_code && Array.isArray(error.response.data.verification_code)) {
        const codeErrors = error.response.data.verification_code;
        if (codeErrors.some(err => err.includes('invalid') || err.includes('expired'))) {
          throw { error: 'invalid_code', message: 'El código de verificación es inválido o ha expirado' };
        }
      }

      // Si hay un mensaje de error genérico
      if (typeof error.response.data === 'object' && error.response.data.error) {
        throw error.response.data;
      }
    }
    
    // Si no pudimos identificar un error específico
    throw error.response?.data || { error: 'unknown_error', message: 'Error al actualizar el email' };
  }
}

export const getLoggedInUser = async () => {
  try {
    // Actualizar también el estado de isAdmin desde el servidor
    await checkAdminStatus(true);
    
    const token = localStorage.getItem('accessToken');
    if (!token) return null;

    const response = await apiClient.get('/auth/me/', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user data:', error);
    return null;
  }
};

/**
 * Verificación de EMERGENCIA del estado de administrador
 * Bypasses todas las capas de caché y consulta directamente al servidor
 */
export const forceAdminVerification = async () => {
  try {
    console.log('[Auth] ⚠️ VERIFICACIÓN DE EMERGENCIA de estado admin');
    
    // Verificar token
    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.log('[Auth] Sin token, no puede ser admin');
      localStorage.setItem('isAdmin', 'false');
      return { isAdmin: false, verified: true };
    }
    
    // Hacer petición directa al servidor, sin usar apiClient para evitar posibles problemas
    const response = await fetch('http://10.122.7.101:8000/api/auth/verify-admin/', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Error del servidor: ${response.status}`);
    }
    
    const data = await response.json();
    const isAdminStatus = data.is_admin === true;
    
    // Actualizar localStorage directamente
    localStorage.setItem('isAdmin', isAdminStatus ? 'true' : 'false');
    
    if (isAdminStatus) {
      localStorage.setItem('maintenance_mode', 'false');
      console.log('[Auth] Admin verificado, desactivando mantenimiento forzosamente');
    }
    
    return {
      isAdmin: isAdminStatus,
      verified: true,
      serverResponse: data
    };
  } catch (error) {
    console.error('[Auth] Error en verificación de emergencia:', error);
    return { 
      isAdmin: false, 
      verified: false,
      error: error.message 
    };
  }
};
